import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectVideoStatus } from '../../features/displayContent/selectors'
import {
	FirstLine,
	FullScreenVideo,
	Img,
	TitleWrapper,
	VideoContainer,
	Wrapper,
} from './CommonComponents'

import theme from '../../styleguide/theme'
import { useTranslation } from 'react-i18next'
import { ClinicalContentSectionsData } from '../../model/model'

const videoTransitionTime = theme.transitions.duration.shortest
const VideoMimeTypeKey = 'video'

const VideoWrapper: React.FC<{
	asset: ClinicalContentSectionsData
}> = ({ asset }) => {
	const [isLoaded, setIsLoaded] = useState(false)
	const videoStatus = useSelector(selectVideoStatus)
	const videoRef = useRef<HTMLVideoElement>(null)
	const isPlayStarted = videoStatus?.playing
	const startTime = videoStatus?.startTime
	const endTime = videoStatus?.endTime
	const isFullscreen = videoStatus?.isFullscreen
	const currentTime = videoStatus?.currentTime
	const assetSrc = asset.mediaUrl

	useEffect(() => {
		if (!videoRef.current) {
			return
		}
		if (currentTime !== undefined) {
			videoRef.current.currentTime = currentTime
		}
		if (isPlayStarted) {
			videoRef.current.play()
		} else {
			videoRef.current.pause()
		}
	}, [isPlayStarted, currentTime])

	const loopTimeVideo = (currentTime: number) => {
		const video = videoRef.current
		if (!video) {
			return
		}
		const { duration } = video
		const shouldVideoLoop =
			(!!endTime && currentTime > endTime) ||
			(!!startTime && currentTime >= duration)

		if (shouldVideoLoop) {
			resetVideoTimeToStartTime()
		}
	}

	const resetVideoTimeToStartTime = () => {
		const video = videoRef.current!
		video.pause()
		video.currentTime = startTime || 0
		setTimeout(() => {
			video.play()
		}, 500)
	}

	return (
		<VideoContainer
			videoTransitionTime={videoTransitionTime}
			fullscreen={isFullscreen}
			loaded={isLoaded}
		>
			{assetSrc ? (
				<FullScreenVideo
					onTimeUpdate={ev => loopTimeVideo(ev.currentTarget.currentTime)}
					src={assetSrc}
					ref={videoRef}
					fullscreen={isFullscreen}
					isPlaying={isPlayStarted}
					muted
					onLoadStart={() => {
						setIsLoaded(false)
					}}
					onCanPlay={() => {
						setIsLoaded(true)
					}}
				/>
			) : null}
		</VideoContainer>
	)
}

const FullContent: React.FC<{
	asset: ClinicalContentSectionsData
	title: string
}> = ({ asset, title }) => {
	const isVideo = asset.mimeType === VideoMimeTypeKey
	return (
		<Wrapper data-testid="clinical-content-sections-component">
			<TitleWrapper>
				<FirstLine>{title}</FirstLine>
			</TitleWrapper>
			{isVideo ? <VideoWrapper asset={asset} /> : <Img src={asset.mediaUrl} />}
		</Wrapper>
	)
}

export const ClinicalContentSections: React.FC<{
	data: ClinicalContentSectionsData
}> = ({ data }) => {
	const { t } = useTranslation()
	const { categoryTag, mediaUrl } = data

	if (categoryTag && mediaUrl) {
		return <FullContent asset={data} title={t(`content.${categoryTag}`)} />
	} else {
		return null
	}
}
